import {API} from "../../../API/API";
import {
  AirportApiAirportListRequest,
  AirportApiGetAirportRequest,
  PageModel
} from "../../../proto/generated/api_entities_pb";
import {InitialPage} from "../../constans/pagination/pagination";
import {PageModelLimits} from "../../constans/pagination/pageModelLimits";
import {DebounceSelectOptionProps} from "../../../components/UI/AntCustom/DebounceSelect";
import {Logger} from "../../logger/Logger";
import {getErrorMessage} from "../../errorHelpers/getCommonErrorMessage";
import {IAirport} from "../../../store/reducers/fleetReducer/fleetReducerTypes";
import {mapTimeZoneEntityToITimezone} from "../../../store/reducers/fleetReducer/fleetMappers";
import {store} from "../../../store/store";

export async function fetchAirports(search?: string, excludeAirportId?: number): Promise<DebounceSelectOptionProps[]> {
  // Если есть поисковый запрос, всегда используем API для поиска по city и другим полям
  // API умеет искать по: ICAO, IATA, name, city, country name
  if (search && search.trim()) {
    const req = new AirportApiAirportListRequest()
      .setQuery(search.trim())
      .setPagemodel(
        new PageModel().setPage(InitialPage).setLimit(PageModelLimits.Select)
      )

    const airportList = await API.getAirportList(req);

    return airportList.getAirportsList()
      .filter(a => !excludeAirportId || a.getAirportid() !== excludeAirportId)
      .map(a => {
        return {
          key: a.getAirportid(),
          label: `${a.getIcaocode() ? a.getIcaocode() + ", " : ""}${a.getIatacode() ? a.getIatacode() + ", " : ""}${a.getName()}`,
          value: a.getAirportid(),
        }
      })
  }

  // Если поискового запроса нет, используем store для быстрого отображения первых результатов
  const state = store.getState();
  const airports = state.airport.airports;

  if (airports && airports.length > 0) {
    const filtered = airports
      .filter(a => {
        // Исключаем аэропорт, если указан excludeAirportId
        if (excludeAirportId && a.value === excludeAirportId) {
          return false;
        }
        return true;
      });
    
    // Ограничиваем количество результатов для производительности
    return filtered.slice(0, PageModelLimits.Select);
  }

  // Если аэропорты еще не загружены в store, делаем запрос (fallback)
  const req = new AirportApiAirportListRequest()
    .setQuery("")
    .setPagemodel(
      new PageModel().setPage(InitialPage).setLimit(PageModelLimits.Select)
    )

  const airportList = await API.getAirportList(req);

  return airportList.getAirportsList()
    .filter(a => !excludeAirportId || a.getAirportid() !== excludeAirportId)
    .map(a => {
      return {
        key: a.getAirportid(),
        label: `${a.getIcaocode() ? a.getIcaocode() + ", " : ""}${a.getIatacode() ? a.getIatacode() + ", " : ""}${a.getName()}`,
        value: a.getAirportid(),
      }
    })
}

export const getInitialAirportForSelect = (airport: IAirport): DebounceSelectOptionProps => {
  return {
    key: airport.id,
    value: airport.id,
    label: `${airport.icaoCode ? airport.icaoCode + ", " : ""}${airport.iataCode ? airport.iataCode + ", " : ""}${airport.name}`,
  }
}

/**
 * Получает опции аэропортов из стора по массиву ID
 * Используется для отображения выбранных аэропортов в селектах
 */
export function getAirportsFromStoreByIds(airportIds: number[]): DebounceSelectOptionProps[] {
  const state = store.getState();
  const airports = state.airport?.airports || [];
  
  return airportIds
    .map(id => airports.find(a => a.value === id))
    .filter(Boolean) as DebounceSelectOptionProps[];
}

export async function fetchInitialAirportForSelect(airportId?: number): Promise<DebounceSelectOptionProps[] | undefined> {
  try {
    if (!airportId)
      throw new Error("No airportId was given")

    const req = new AirportApiGetAirportRequest()
      .setAirportid(airportId);

    const airportEntity = (await API.getAirportById(req)).getAirport();
    if (!airportEntity)
      throw new Error("No airportEntity inside getAirportById response")

    const timezoneEntity = airportEntity.getTimezone();
    if (!timezoneEntity)
      throw new Error("No timezoneEntity inside getAirportById response")

    const airport: IAirport = {
      id: airportEntity?.getAirportid(),
      name: airportEntity.getName(),
      icaoCode: airportEntity.getIcaocode(),
      iataCode: airportEntity.getIatacode(),
      countryName: airportEntity.getCountryname(),
      timezone: mapTimeZoneEntityToITimezone(timezoneEntity),
    }

    return [getInitialAirportForSelect(airport)]
  } catch (e) {
    Logger.error("Error while getting airport by id for select: " + getErrorMessage(e));
  }
}